import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Button, Tooltip, IconButton } from '@mui/material';
import styles from './styles.module.css';
import DataTable from 'components/utils/DataTable';
import SalesPersonFormDialog from 'components/popups/SalesPersonFormDialog';
import SalesPersonService from 'services/SalesPersonService';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import SearchIcon from '@mui/icons-material/Search';
import Info from 'components/utils/Alert/Info';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import { ShopContext } from 'Context/ShopContext';
import ConfirmPopup from 'components/utils/Alert/ConfirmPopup';
import EditIcon from '@mui/icons-material/Edit';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import { AlertMessagesContext } from 'react-alert-messages';
import { useTranslation } from 'react-i18next';

function SalesPersons() {
  const { t: tCommon } = useTranslation('common');
  const { t: tManagement } = useTranslation('management');
  const { shop } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const [salesPersons, setSalesPersons] = useState(null);
  const [salesPersonsFiltered, setSalesPersonsFiltered] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [isLoading, setLoading] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState(null);
  const [selectedForEdit, setSelectedForEdit] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  const handleSalesPersonActionClick = (salesPerson) => setSelectedForDelete(salesPerson);
  const handleEditClick = (salesPerson) => setSelectedForEdit(salesPerson);
  const handleAddClick = () => setShowAddForm(true);

  const handleDeleteSalesPerson = async () => {
    setLoading(true);
    try {
      const data = { ...selectedForDelete, is_active: !selectedForDelete.is_active };
      const resp = await SalesPersonService.updateSalesPersons(selectedForDelete.id, data);
      getSalesPersons();
      postAlertMessage({
        text:
          resp?.is_active === true
            ? tManagement('Salesperson activated successfully')
            : tManagement('Salesperson deactivated successfully'),
        type: 'success',
      });
    } catch (err) {
      postAlertMessage({
        text: err.message || 'An error occurred',
        type: 'error',
      });
    }
    setLoading(false);
    setSelectedForDelete(null);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    const filterResponse = salesPersons
      ? salesPersons.filter(
          (salesPerson) =>
            salesPerson.name.toLowerCase().includes(value.toLowerCase()) ||
            salesPerson.username.toLowerCase().includes(value.toLowerCase()) ||
            salesPerson.id.toString().includes(value)
        )
      : [];
    setSalesPersonsFiltered(filterResponse);
  };

  const getSalesPersons = useCallback(async () => {
    setLoading(true);
    try {
      const salesPersons = await SalesPersonService.getSalesPersons();
      setSalesPersons(salesPersons);
    } catch (err) {
      postAlertMessage({
        text: err.message || 'Failed to fetch salespersons',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [postAlertMessage]);

  const headerData = [
    {
      label: tCommon('ID'),
      id: 'id',
      type: 'text',
    },
    {
      label: tCommon('Name'),
      id: 'name',
      type: 'text',
    },
    {
      label: tCommon('Username'),
      id: 'username',
      type: 'text',
    },
    {
      label: tCommon('Password'),
      id: 'password',
      type: 'text',
    },
    {
      label: tCommon('Active'),
      id: 'is_active',
      type: 'callback',
      viewRender: (obj) => {
        return obj.is_active === true ? 'Yes' : 'No';
      },
    },
    {
      label: tCommon('Actions'),
      id: 'actions',
      type: 'callback',
      viewRender: (item) => {
        return (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Tooltip title="Edit">
              <IconButton aria-label="Edit" onClick={() => handleEditClick(item)} size="small">
                <EditIcon className={styles.iconButton} />
              </IconButton>
            </Tooltip>
            <Tooltip title={item.is_active ? tCommon('Deactivate') : tCommon('Activate')}>
              <IconButton
                onClick={(e) => {
                  handleSalesPersonActionClick(item);
                  e.stopPropagation();
                }}
                color={item.is_active ? 'primary' : 'default'}
              >
                {item.is_active ? <ToggleOnIcon fontSize="large" /> : <ToggleOffIcon fontSize="large" />}
              </IconButton>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    if (!shop?.id) return;
    getSalesPersons();
  }, [getSalesPersons, shop?.id]);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <h2 className={styles.title}>
          {tCommon('Salespersons')} <span className={styles.menuTitle}>{tCommon('Management')}</span>
        </h2>
      </div>
      <div className={styles.changeable}>
        <div className={styles.filterSec}>
          <h2 className={styles.subTitle}>{tCommon('Salespersons')}</h2>

          <div className={styles.filerInputSec}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              style={{ backgroundColor: '#ff851b' }}
              className={styles.actionBtn}
              onClick={handleAddClick}
            >
              <NoteAddIcon className={styles.actionBtnIcon} />
              {tCommon('New Salesperson')}
            </Button>
            <div className={styles.searchSec}>
              <input
                type="text"
                value={searchText}
                onChange={(e) => {
                  handleSearch(e.target.value);
                }}
                className={styles.searchInput}
                placeholder={tCommon('Search items')}
              />
              <Tooltip title="Search" placement="bottom">
                <IconButton size="small" onClick={handleSearch}>
                  <SearchIcon className={styles.searchIcon} />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </div>
      </div>

      {(salesPersons || isLoading) &&
        ((salesPersons && salesPersons.length) || isLoading ? (
          <DataTable
            columns={headerData}
            rows={salesPersonsFiltered ? salesPersonsFiltered : salesPersons || []}
            isLoading={isLoading}
          />
        ) : (
          <Info
            title={tManagement('No salespersons to list')}
            content={tManagement(
              'You have no salespersons to list with current filter configuration. Please clear the filters or create a new salesperson'
            )}
          />
        ))}
      {selectedForEdit && (
        <SalesPersonFormDialog
          handleClose={() => setSelectedForEdit(null)}
          handleSuccess={() => {
            setSelectedForEdit(null);
            getSalesPersons();
          }}
          salesPerson={selectedForEdit}
        />
      )}
      {showAddForm && (
        <SalesPersonFormDialog
          handleClose={() => setShowAddForm(false)}
          handleSuccess={() => {
            setShowAddForm(false);
            getSalesPersons();
          }}
        />
      )}
      {selectedForDelete && (
        <ConfirmPopup
          data={selectedForDelete}
          handleClose={() => setSelectedForDelete(null)}
          handleOkay={handleDeleteSalesPerson}
          alertTitle={selectedForDelete.is_active ? tManagement('Confirm deactivate') : tManagement('Confirm activate')}
          successBtnName={selectedForDelete.is_active ? tCommon('Deactivate') : tCommon('Activate')}
          alertContent={
            selectedForDelete.is_active
              ? tManagement('Confirm deactive salesperson')
              : tManagement('Confirm active salesperson')
          }
        />
      )}
    </div>
  );
}

export default withConsoleBase(SalesPersons);
